import ky from 'ky'
import {ResponseError} from '@/services/response-error.js'
import {getURLSearchParams} from '@/helpers/url.js'

/**
 * Http service class – This is a base wrapper around ky, and facilitates Ky
 * setup, request & response handling, and error processing. It should not
 * be used directly, but instead via the API service classes which provide an
 * additional layer of abstraction, shared logic and convenience helpers.
 */
class HttpService {

  /**
   * Constructor
   */
  constructor() {
    this.setupService()
  }

  /**
   * Setup service
   */
  setupService() {
    this.ky = ky.create({
      timeout: 27500, //Ky uses 10000 by default
      retry: {
        statusCodes: [408, 502, 503, 504], //Ky includes 500 by default
      },
    })
  }

  /**************************************************************************
   * Request & response handling
   ***/

  /**
   * Generic request wrapper
   */
  async request(url, config = {}) {

    //Process config
    this.appendJsonHeaders(config)
    this.appendSearchParams(config)
    this.appendCacheControl(config)

    //Perform request
    try {
      const response = await this.ky(url, config)
      const data = await this.processSuccessfulResponse(response, config)
      return data
    }
    catch (error) {
      await this.processFailedResponse(error, config)
    }
  }

  /**
   * Get response data
   */
  async getResponseData(response) {
    const contentType = response.headers.get('Content-Type')
    if (contentType && contentType.includes('application/json')) {
      return await response.json()
    }
    return await response.text()
  }

  /**
   * Process response
   */
  async processSuccessfulResponse(response, config) {

    //Get data
    const data = await this.getResponseData(response)

    //Successful response (2xx) from server, but actually an error?
    if (data && typeof data === 'object' && (data.errors || data.error)) {
      throw this.createError(response, config, data)
    }

    //Return data
    return data
  }

  /**
   * Process error response
   */
  async processFailedResponse(responseError, config) {

    //Not a response error? Process as is
    //Could have been thrown from successful response processing
    if (!responseError.response) {
      await this.processError(responseError)
      return
    }

    //Get response data
    const {response} = responseError
    const data = await this.getResponseData(response)

    //Process error
    const error = this.createError(response, config, data)
    await this.processError(error)
  }

  /**
   * Create error
   */
  createError(response, config, data) {
    return new ResponseError(response, config, data)
  }

  /**
   * Process error
   */
  async processError(error) {

    //No data, rethrow as is
    const {data} = error
    if (typeof data !== 'object') {
      throw error
    }

    //Normalize errors in data to always be an array
    if (data.error && typeof data.error === 'string') {
      data.errors = [data.error]
    }
    else if (data.errors && typeof data.errors === 'string') {
      data.errors = [data.errors]
    }

    //Rethrow
    throw error
  }

  /**************************************************************************
   * Config helpers
   ***/

  /**
   * Append JSON headers
   */
  appendJsonHeaders(config) {
    if (config.json) {
      config.headers ??= {}
      config.headers['Content-Type'] = 'application/json'
    }
  }

  /**
   * Append search params
   */
  appendSearchParams(config) {
    if (config.params) {
      config.searchParams ??= getURLSearchParams(config.params)
    }
  }

  /**
   * Append cache control
   */
  appendCacheControl(config) {
    if (config.ignoreCache) {
      config.cache ??= 'no-store'
    }
  }
}

//Export singleton instance
export const http = new HttpService()
